import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Compass, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, fmtTimeRange } from "@/lib/format";
import { SaidSections, AlignmentCompass } from "@/components/gauge/ReflectionView";

// Upcoming sessions where the client chose to share a GAUGE reflection with this enhancer.
export default function SessionPrep({ bookings = [] }) {
  const { data: shared = [] } = useQuery({ queryKey: ["enh-gauge-shared"], queryFn: () => api.get("/enhancer/me/gauge-shared").then((r) => r.data) });
  const [open, setOpen] = useState(null);
  const latest = {};
  shared.forEach((e) => { if (!latest[e.member_id] || e.created_at > latest[e.member_id].created_at) latest[e.member_id] = e; });
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= today && !["cancelled", "completed", "no_show"].includes(b.status) && latest[b.user_id])
    .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)).slice(0, 10);
  return (
    <section className="mb-8" data-testid="session-prep">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><Compass size={20} className="text-[#D98E73]" /> Session prep</h2>
      <p className="text-xs text-[#4A5D6B] mb-4">Upcoming clients who chose to share their GAUGE reflection with you. These are their words, shared by choice — not a diagnosis.</p>
      {upcoming.length === 0 ? <p className="text-sm text-[#8FA89B] eyw-card p-4" data-testid="session-prep-empty">No upcoming client has shared a reflection yet. Members are offered this when they book with you.</p> : (
        <div className="space-y-3">
          {upcoming.map((b) => {
            const e = latest[b.user_id];
            const isOpen = open === b.id;
            return (
              <div key={b.id} className="eyw-card p-4" data-testid={`session-prep-${b.id}`}>
                <button onClick={() => setOpen(isOpen ? null : b.id)} aria-expanded={isOpen} data-testid={`session-prep-open-${b.id}`} className="w-full flex flex-wrap items-center justify-between gap-2 text-left">
                  <span><b className="text-[#1A242B]">{e.member_name || b.name}</b> <span className="text-sm text-[#4A5D6B]">· {b.service_title} · {fmtDate(b.date)} {fmtTimeRange(b.start_time)}</span></span>
                  <span className="text-xs text-[#144F75] inline-flex items-center gap-1">{e.path_title} <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} /></span>
                </button>
                {isOpen && (
                  <div className="mt-4 space-y-4">
                    {e.member_reflection && (
                      <div className="text-sm text-[#4A5D6B] space-y-1">
                        {e.member_reflection.intention && <p><b className="text-[#1A242B]">Their intention:</b> {e.member_reflection.intention}</p>}
                        {e.member_reflection.chosen_next_step && <p><b className="text-[#1A242B]">Their chosen next step:</b> {e.member_reflection.chosen_next_step}</p>}
                      </div>
                    )}
                    {e.reflection?.said?.length ? <><AlignmentCompass said={e.reflection.said} /><p className="font-serif text-lg text-[#1A242B]">In their words</p><SaidSections said={e.reflection.said} /></>
                      : <p className="text-sm text-[#4A5D6B]">Focus areas: {(e.top_focus_areas || []).join(", ") || "—"}</p>}
                    {(e.followups || []).slice(-2).map((f) => <p key={f.id} className="text-sm text-[#4A5D6B]"><b className="text-[#1A242B]">Since then:</b> {[f.tried, f.happened, f.adjust].filter(Boolean).join(" · ")}</p>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
